// Approximate, non-clinical wake-window guidance (time a baby is typically comfortably
// awake before the next nap / before bedtime), aligned to the same ageWeeks breakpoints
// as richNorms in ./norms.ts. Sourced from commonly published pediatric-sleep-consultant
// ranges, not a personalized or medical measurement — see the Medical Disclaimer in
// docs/product-latest.md. Kept deliberately separate from DevelopmentalNorm: norms can be
// overridden per-week from a `developmentalNorms/{ageWeeks}` Firestore doc that wholesale
// replaces the bundled record (see App.tsx), so folding these fields into DevelopmentalNorm
// would make them silently vanish for any admin-overridden week.
export interface WakeWindowRange {
  minMin: number;
  maxMin: number;
}

export interface AgeWakeWindowNorm {
  ageWeeks: number;
  nap: WakeWindowRange;
  night: WakeWindowRange;
}

export const wakeWindowNorms: AgeWakeWindowNorm[] = [
  { ageWeeks: 0, nap: { minMin: 45, maxMin: 60 }, night: { minMin: 60, maxMin: 90 } },
  { ageWeeks: 4, nap: { minMin: 60, maxMin: 75 }, night: { minMin: 75, maxMin: 105 } },
  { ageWeeks: 6, nap: { minMin: 60, maxMin: 90 }, night: { minMin: 90, maxMin: 120 } },
  { ageWeeks: 8, nap: { minMin: 75, maxMin: 105 }, night: { minMin: 105, maxMin: 135 } },
  { ageWeeks: 12, nap: { minMin: 90, maxMin: 120 }, night: { minMin: 120, maxMin: 150 } },
  { ageWeeks: 16, nap: { minMin: 105, maxMin: 135 }, night: { minMin: 150, maxMin: 180 } },
  { ageWeeks: 20, nap: { minMin: 120, maxMin: 150 }, night: { minMin: 165, maxMin: 195 } },
  { ageWeeks: 24, nap: { minMin: 135, maxMin: 165 }, night: { minMin: 180, maxMin: 210 } },
  { ageWeeks: 32, nap: { minMin: 150, maxMin: 180 }, night: { minMin: 195, maxMin: 225 } },
  { ageWeeks: 36, nap: { minMin: 165, maxMin: 195 }, night: { minMin: 210, maxMin: 240 } },
  { ageWeeks: 40, nap: { minMin: 180, maxMin: 210 }, night: { minMin: 225, maxMin: 255 } },
  { ageWeeks: 52, nap: { minMin: 210, maxMin: 240 }, night: { minMin: 240, maxMin: 270 } },
  { ageWeeks: 64, nap: { minMin: 240, maxMin: 300 }, night: { minMin: 270, maxMin: 330 } },
  { ageWeeks: 78, nap: { minMin: 240, maxMin: 300 }, night: { minMin: 300, maxMin: 360 } },
  // At 92+ weeks many toddlers have dropped naps entirely, so the true pre-bedtime wake
  // window can range from ~4h (still napping) to ~11h+ (nap-free); the range below is
  // intentionally wide at cold start and narrows quickly once personal data blends in.
  { ageWeeks: 92, nap: { minMin: 240, maxMin: 360 }, night: { minMin: 330, maxMin: 420 } },
  { ageWeeks: 104, nap: { minMin: 240, maxMin: 360 }, night: { minMin: 360, maxMin: 420 } },
];

// Returns the wakeWindowNorms entry for the largest ageWeeks value <= week.
export function getWakeWindowNormForWeek(week: number): AgeWakeWindowNorm {
  let best = wakeWindowNorms[0];
  for (const n of wakeWindowNorms) {
    if (n.ageWeeks <= week) best = n;
    else break;
  }
  return best;
}
